if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      // ================ FRACTIONAL QUANTITIES =========================
      /*
        parse fractional quantities
        multiply yards by unitsPerYard to get units
        round to nearest number
        guard against floating errors, round to nearest number.
        if units < 1, set to 1
        set quantity to units
        Set private property for unitsPerYard (not visible)
        Public property of total yardage to inform user of total yardage when at checkout (and cart/cartdrawer, etc)
      */
      try {
        const unitsPerYardRaw = this.dataset.unitsPerYard;
        if (unitsPerYardRaw) {
          const unitsPerYard = parseFloat(String(unitsPerYardRaw).replace(',', '.'));
          if (Number.isFinite(unitsPerYard) && unitsPerYard > 0) {
            const qtyInput = this.form.querySelector('input[name="quantity"]');
            if (qtyInput && qtyInput.value !== undefined && qtyInput.value !== null && qtyInput.value !== '') {
              const yards = parseFloat(String(qtyInput.value).replace(',', '.'));
              if (Number.isFinite(yards)) {
                let units = yards * unitsPerYard;
                units = Math.round(units + Number.EPSILON);
                if (units < 1) units = 1;
                formData.set('quantity', String(units));
                try {
                  formData.set('properties[_units_per_yard]', String(unitsPerYard));
                  const totalYards = units / unitsPerYard;
                  formData.set('properties[Total yardage]', `${totalYards.toFixed(2)} yards`);
                } catch (e) { }
              }
            }
          }
        }
      } catch (e) {
        // non-fractional quantities fall through 
      }
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message });
            this.handleErrorMessage(response.description);

            // Shopify may still add the max available qty while returning 422.
            // Refresh + correct Total yardage when the error indicates a clamp.
            const errorText = `${response.message || ''} ${response.description || ''}`;
            const likelyPartialAdd =
              response.status === 422 &&
              (/were added to your cart/i.test(errorText) ||
                /can('|’)t add more/i.test(errorText) ||
                /can only add/i.test(errorText) ||
                (formData.get('properties[_units_per_yard]') &&
                  !/sold out/i.test(errorText)));
            if (likelyPartialAdd && this.cart) {
              return this.refreshCartAfterPartialAdd(
                formData,
                response.message || response.description || ''
              );
            }

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        });
    }

    refreshCartAfterPartialAdd(formData, partialMessage) {
      const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
      const variantId = String(formData.get('id'));
      const requestedYardageProp = formData.get('properties[Total yardage]');
      const unitsPerYardProp = formData.get('properties[_units_per_yard]');
      const requestedQty = Number.parseInt(String(formData.get('quantity') || ''), 10);

      const sectionsPromise = fetch(
        `${window.location.pathname}?sections=${sectionIds.join(',')}`
      ).then((r) => r.json());

      const cartPromise = fetch(`${routes.cart_url}.js`).then((r) => r.json());

      // Phase 1 (foreground): wait for both, render drawer, populate line-item error.
      return Promise.all([sectionsPromise, cartPromise]).then(([sections, cartState]) => {
        this.error = false;
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
        });

        // Locate the matching fabric line. Prefer exact yardage match; fall back to
        // newest line for this variant (inventory clamp may leave stale yardage).
        let lineIndex = -1;
        let line = null;
        for (let i = cartState.items.length - 1; i >= 0; i--) {
          const item = cartState.items[i];
          if (String(item.variant_id) !== variantId) continue;
          const yardageMatches =
            !requestedYardageProp ||
            (item.properties && item.properties['Total yardage'] === requestedYardageProp);
          if (yardageMatches || lineIndex < 0) {
            lineIndex = i + 1; // Shopify uses 1-based indices for line item IDs
            line = item;
            if (yardageMatches) break;
          }
        }

        const renderAndAnnotate = () => {
          this.cart.renderContents({ sections });
          this.setLineItemError(lineIndex, partialMessage);
        };

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => {
            setTimeout(renderAndAnnotate);
          }, { once: true });
          quickAddModal.hide(true);
        } else {
          renderAndAnnotate();
        }

        // Phase 2 (background): correct yardage when qty was clamped or property is stale.
        if (!line || !unitsPerYardProp) return;
        const unitsPerYard = parseFloat(String(unitsPerYardProp).replace(',', '.'));
        if (!Number.isFinite(unitsPerYard) || unitsPerYard <= 0) return;

        const correctedYardage = `${(line.quantity / unitsPerYard).toFixed(2)} yards`;
        const currentYardage = line.properties && line.properties['Total yardage'];
        const qtyWasClamped =
          Number.isFinite(requestedQty) && line.quantity !== requestedQty;
        if (correctedYardage === currentYardage && !qtyWasClamped) return;

        return fetch(`${routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            id: line.key,
            quantity: line.quantity,
            properties: {
              ...line.properties,
              _units_per_yard: String(unitsPerYard),
              'Total yardage': correctedYardage,
            },
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        })
          .then((r) => r.json())
          .then((updatedState) => {
            this.cart.renderContents(updatedState);
            this.setLineItemError(lineIndex, partialMessage);
          })
          .catch(() => {});
      });
    }

    setLineItemError(lineIndex, message) {
      if (!message || lineIndex < 1) return;
      const errorEl =
        document.getElementById(`CartDrawer-LineItemError-${lineIndex}`) ||
        document.getElementById(`Line-item-error-${lineIndex}`);
      if (!errorEl) return;
      const textEl = errorEl.querySelector('.cart-item__error-text');
      if (textEl) textEl.innerHTML = message;
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
